import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(iso: string | Date | null | undefined): string {
  if (!iso) return '—';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null || !Number.isFinite(bytes)) return '—';
  const abs = Math.abs(bytes);
  if (abs < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB', 'PB'];
  let v = bytes / 1024;
  let i = 0;
  while (Math.abs(v) >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  const decimals = Math.abs(v) >= 10 ? 1 : 2;
  return `${v.toFixed(decimals)} ${units[i]}`;
}

export function formatUptime(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return '—';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// Countdown for a removal due date: "in 6d", "in 5h", or "due" once passed.
export function removalDueLabel(iso: string | null | undefined): string {
  if (!iso) return '—';
  const ms = Date.parse(iso) - Date.now();
  if (Number.isNaN(ms)) return '—';
  if (ms <= 0) return 'due';
  const days = Math.floor(ms / 86_400_000);
  return days >= 1 ? `in ${days}d` : `in ${Math.max(1, Math.floor(ms / 3_600_000))}h`;
}

// Days until a login ends, once that's close enough to act on. Same 3 days as the
// CLI's own warning and the plugin's notice. null = not soon, or not known.
export function loginDaysLeft(endsAt: string | null | undefined): number | null {
  if (!endsAt) return null;
  const left = new Date(endsAt).getTime() - Date.now();
  if (Number.isNaN(left) || left > 3 * 24 * 60 * 60 * 1000) return null;
  return Math.max(0, Math.ceil(left / (24 * 60 * 60 * 1000)));
}

export function relativeTime(iso: string | Date | null | undefined): string {
  if (!iso) return '—';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '—';
  const diff = d.getTime() - Date.now();
  const absSec = Math.abs(diff) / 1000;
  const units: [number, Intl.RelativeTimeFormatUnit][] = [
    [60, 'second'],
    [60, 'minute'],
    [24, 'hour'],
    [7, 'day'],
    [4.34524, 'week'],
    [12, 'month'],
    [Number.POSITIVE_INFINITY, 'year'],
  ];
  let v = absSec;
  let i = 0;
  for (; i < units.length - 1; i++) {
    if (v < units[i][0]) break;
    v /= units[i][0];
  }
  const sign = diff < 0 ? -1 : 1;
  return new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' }).format(
    Math.round(sign * v),
    units[i][1]
  );
}
